import axios from 'axios';
import { User } from '../types';
import { API_BASE_URL } from '../utils/network';
import { debugLog } from '../utils/debug';
import { normalizeApiError } from '../utils/apiError';
import {
  clearStoredAuthSession,
  expireAuthSession,
  getStoredAuthToken,
  isAuthFailureResponse,
  markAuthSessionActive
} from '../utils/authSession';

debugLog('API Configuration:', {
  API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL
});

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getStoredAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    debugLog('API Response:', {
      url: response.config.url,
      status: response.status,
      statusText: response.statusText
    });
    return response;
  },
  (error) => {
    const normalizedError = normalizeApiError(error, 'Błąd komunikacji z API');
    console.error('🚨 API Error:', {
      ...normalizedError,
      url: error.config?.url,
      baseURL: error.config?.baseURL
    });

    if (isAuthFailureResponse(error.response?.status, error.response?.data)) {
      expireAuthSession(`api:${error.config?.url || 'unknown'}`);
    }
    return Promise.reject(error);
  }
);

export const setAuthToken = (token: string): void => {
  markAuthSessionActive();
  localStorage.setItem('authToken', token);
};

export const removeAuthToken = (): void => {
  clearStoredAuthSession();
};

export const getAuthToken = (): string | null => {
  return getStoredAuthToken();
};

export const setUser = (user: User): void => {
  localStorage.setItem('user', JSON.stringify(user));
};

export const getUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    clearStoredAuthSession();
    return null;
  }
};

export default api;
